import React from "react";
import { createPortal } from "react-dom";
import type { ExportProgress } from "../utils/exportGridToExcel";

const STAGE_LABEL: Record<ExportProgress["stage"], string> = {
  preparing: "Preparing data…",
  writing: "Writing rows to Excel…",
  compressing: "Compressing workbook…",
  saving: "Starting download…",
};

interface Props {
  progress: ExportProgress | null;
  error?: string | null;
  onCancel: () => void;
  onDismissError?: () => void;
}

const fmt = (n: number) => n.toLocaleString("en-IN");

/**
 * Export ke dauraan full-screen progress. Bade reports mein 2-3 minute lag
 * sakte hain, isliye rows count aur cancel dono dikhana zaroori hai.
 */
const ExportProgressOverlay: React.FC<Props> = ({
  progress,
  error,
  onCancel,
  onDismissError,
}) => {
  if (!progress && !error) return null;

  const percent = progress?.percent ?? 0;
  // "compressing" ke dauraan rows to 100% ho chuki hoti hain par zip abhi
  // baaki hai — bar ko 100% dikhana galat lagta hai.
  const barPercent =
    progress?.stage === "compressing" || progress?.stage === "saving"
      ? 100
      : percent;
  const indeterminate = progress?.stage === "compressing";

  // Portal isliye ki kisi bhi ancestor par transform/filter ho to position:fixed
  // us ancestor ke relative ho jaata hai aur overlay jagah se hat jaata hai.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Excel export progress"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <style>{`
        @keyframes expBarStripes {
          from { background-position: 0 0; }
          to   { background-position: 32px 0; }
        }
        .exp-bar-anim {
          background-image: linear-gradient(45deg,
            rgba(255,255,255,0.25) 25%, transparent 25%,
            transparent 50%, rgba(255,255,255,0.25) 50%,
            rgba(255,255,255,0.25) 75%, transparent 75%, transparent);
          background-size: 32px 32px;
          animation: expBarStripes 0.8s linear infinite;
        }
      `}</style>

      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          width: "min(440px, 100%)",
          padding: "24px",
        }}
      >
        {error ? (
          <>
            <h6 style={{ margin: "0 0 8px", fontWeight: 700, color: "#b91c1c" }}>
              Export failed
            </h6>
            <p style={{ margin: "0 0 18px", fontSize: "13px", color: "#475569" }}>
              {error}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onDismissError}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <h6 style={{ margin: "0 0 4px", fontWeight: 700, color: "#0f172a" }}>
              Exporting to Excel
            </h6>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
              {STAGE_LABEL[progress!.stage]}
            </p>

            <div
              style={{
                height: "12px",
                borderRadius: "999px",
                background: "#e2e8f0",
                overflow: "hidden",
              }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={indeterminate ? undefined : percent}
            >
              <div
                className={indeterminate ? "exp-bar-anim" : undefined}
                style={{
                  height: "100%",
                  width: `${barPercent}%`,
                  background: "#2563eb",
                  borderRadius: "999px",
                  transition: "width 0.2s linear",
                }}
              />
            </div>

            <div
              style={{
                marginTop: "10px",
                display: "flex",
                justifyContent: "space-between",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              <span>
                {fmt(progress!.written)} / {fmt(progress!.total)} rows
              </span>
              <span style={{ fontWeight: 700, color: "#0f172a" }}>{percent}%</span>
            </div>

            <p
              style={{
                margin: "14px 0 16px",
                fontSize: "11px",
                color: "#94a3b8",
              }}
            >
              Poori report ek hi sheet mein jaa rahi hai — koi page break nahi.
              Aap tab tak page use kar sakte hain.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ExportProgressOverlay;
